<template>
    <settings-wrapper title="Space settings" section="General">
        <v-row>
            <v-col cols="12" md="6">
                <v-card>
                    <v-card-title>Space details</v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col>
                                <label>Name</label>
                                <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">{{ currentSpaceName }}</v-sheet>
                            </v-col>
                            <v-col>
                                <label>Description</label>
                                <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">{{ getSpaceDescription }}</v-sheet>
                            </v-col>
                        </v-row>
                        <v-row v-if="currentSpaceType === spaceTypes.EDUCATION_SPACE">
                            <v-col cols="6">
                                <label>Archive on</label>
                                <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">
                                    <span class="subtitle-2 secondary--text">{{ spaceArchivalDate }}</span>
                                </v-sheet>
                            </v-col>
                        </v-row>
                    </v-card-text>
                    <v-card-actions>
                        <space-details-edit-dialog
                            v-if="isInstanceEditor"
                            :space-name="currentSpaceName"
                            :space-description="getSpaceDescription"
                            :archive-by-date="spaceArchivalDate"
                            :max-archive-by-date="furthestArchivalDate"
                            :show-archive-by-date="currentSpaceType === spaceTypes.EDUCATION_SPACE"
                            :can-edit-archive-by-date="isInstanceEditor && isSpaceAdmin && !isSpaceArchived"
                            @updated="refreshSpaceDetails" />
                    </v-card-actions>
                </v-card>
            </v-col>
            <v-col cols="12" md="6">
                <v-card>
                    <v-card-title>Space status</v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col cols="12" md="6">
                                <label>Visibility</label>
                                <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">
                                    <span class="subtitle-2 secondary--text" v-if="currentSpaceVisibility === spaceVisibilityCodes.PUBLIC_SPACE">Public</span>
                                    <span class="subtitle-2 secondary--text" v-if="currentSpaceVisibility === spaceVisibilityCodes.PRIVATE_SPACE">Private</span>
                                    <span class="subtitle-2 secondary--text" v-if="currentSpaceVisibility === spaceVisibilityCodes.FACULTY_ONLY_SPACE">
                                        Faculty only
                                    </span>
                                </v-sheet>
                            </v-col>
                            <v-col cols="12" md="6">
                                <label>Lifecycle phase</label>
                                <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">
                                    <span class="subtitle-2 secondary--text" v-if="!isSpaceArchived">Live</span>
                                    <span class="subtitle-2 secondary--text" v-if="isSpaceArchived">
                                        Archived on {{ spaceArchiveTimestamp | dateTimeToHuman }}
                                    </span>
                                </v-sheet>
                            </v-col>
                            <v-col cols="6" v-if="isSpaceAdmin">
                                <label>Role</label>
                                <v-sheet color="grey lighten-4 pa-3" rounded class="d-flex align-center">
                                    <v-icon small color="secondary" class="mr-1">security</v-icon>
                                    <span class="subtitle-2 secondary--text">Administrator</span>
                                </v-sheet>
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <h3 class="error--text">Danger Zone</h3>
            </v-col>
        </v-row>

        <v-row>
            <v-col v-if="isSpaceAdmin" cols="12" md="6">
                <v-card>
                    <v-card-title>Delete space</v-card-title>
                    <v-card-actions>
                        <EntityDeleteDialog entity-type="space" :organization-id="activeOrgId" :space-id="activeSpaceId" :space-name="currentSpaceName" />
                    </v-card-actions>
                </v-card>
            </v-col>
            <v-col cols="12" :md="isSpaceAdmin ? 6 : 12">
                <v-card>
                    <v-card-title>Leave space</v-card-title>
                    <v-card-actions>
                        <EntityLeaveDialog entity-type="space" />
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
    </settings-wrapper>
</template>

<script>
import { mapState } from 'vuex'
import { enumsData } from '@/mixins/enums'
import { DateTime } from 'luxon'
import { isAdminRole, isEditorRole } from '@/utils'
import SettingsWrapper from '@/components/SettingsWrapper.vue'
import EntityLeaveDialog from '@/components/EntityLeaveDialog.vue'
import EntityDeleteDialog from '@/components/EntityDeleteDialog.vue'

const SpaceDetailsEditDialog = () => import('../components/SpaceDetailsEditDialog.vue')

export default {
    name: 'SpaceConfiguration',
    mixins: [enumsData],
    components: {
        SettingsWrapper,
        SpaceDetailsEditDialog,
        EntityLeaveDialog,
        EntityDeleteDialog
    },
    async created() {
        if (this.activeOrgId && !this.spacesWithPrimarySnapshots.length) {
            await this.$store.dispatch('orgStore/fetchOrgSpaces', this.activeOrgId)
        }
        if (this.activeSpaceId) {
            this.loadActiveSpaceContext()
        }
    },
    methods: {
        refreshSpaceDetails(data) {
            this.$store.commit('spaceStore/setCurrentSpace', data)
            this.$store.dispatch('orgStore/fetchOrgSpaces', this.activeOrgId)
        },
        async loadActiveSpaceContext() {
            if (!this.activeSpaceId) {
                return
            }
            try {
                const { data } = await this.$axios.get(`/spaces/${this.activeSpaceId}`)
                this.$store.commit('spaceStore/setCurrentSpace', data)
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Failed to load selected space configuration.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            }
        }
    },
    computed: {
        ...mapState('orgStore', ['spacesWithPrimarySnapshots']),
        ...mapState('spaceStore', ['currentSpace']),
        activeOrgId() {
            return this.$route.params.oid
        },
        activeSpaceId() {
            return this.$route.params.sid
        },
        activeSpaceData() {
            if (!this.activeSpaceId) {
                return null
            }
            return this.spacesWithPrimarySnapshots.find(space => space.sid.toString() === this.activeSpaceId.toString()) || null
        },
        isSpaceAdmin() {
            return isAdminRole(this.activeSpaceData?.space_role)
        },
        isInstanceEditor() {
            const role = this.activeSpaceData?.space_role
            return isAdminRole(role) || isEditorRole(role)
        },
        currentSpaceType() {
            return this.activeSpaceData?.space_type || null
        },
        currentSpaceVisibility() {
            return this.activeSpaceData?.space_visibility_type || null
        },
        isSpaceArchived() {
            return Boolean(
                this.currentSpace?.archival_timestamp || this.currentSpace?.space_archival_timestamp || this.activeSpaceData?.space_archival_timestamp
            )
        },
        currentSpaceName() {
            return this.currentSpace?.long_id || this.activeSpaceData?.space_long_id || ''
        },
        spaceArchivalDate() {
            return this.activeSpaceData?.space_archive_by_date || null
        },
        spaceCreationDate() {
            return this.activeSpaceData?.space_creation_timestamp || null
        },
        getSpaceDescription() {
            return this.activeSpaceData?.space_description || 'No description available.'
        },
        spaceArchiveTimestamp() {
            return this.activeSpaceData?.space_archival_timestamp || null
        },
        furthestArchivalDate() {
            if (!this.spaceCreationDate) {
                return null
            }
            return DateTime.fromISO(this.spaceCreationDate).plus({ years: 1 }).toISODate()
        }
    },
    watch: {
        spacesWithPrimarySnapshots: {
            immediate: true,
            handler(spaces) {
                if (!this.$route.params.sid && Array.isArray(spaces) && spaces.length) {
                    this.$router.replace({
                        name: this.$route.name,
                        params: {
                            ...this.$route.params,
                            sid: spaces[0].sid
                        },
                        query: { ...this.$route.query }
                    })
                }
            }
        },
        activeSpaceId: {
            immediate: true,
            handler() {
                if (this.activeSpaceId) {
                    this.loadActiveSpaceContext()
                }
            }
        }
    }
}
</script>
